import { FC, Suspense, useState } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';

import { useGetUserProfile } from '@/api/user-dashboard/queries';
import { GetAppBanner } from '@/features/get-app-banner';
import { FeatureFlags, useFeatureFlag } from '@/global-hooks/use-feature-flag-enabled';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { useScrollToTop } from '@/global-hooks/use-scroll-to-top';
import { PRE_IPO_TABS } from '@/pages/deals/pre-ipo-v2/tabs';
import { useDealBoardViewerPerspective } from '@/pages/deals-pipeline/use-deal-board-viewer-perspective';
import { DEALS_TAB_KEYS } from '@/routes/deals-tabs';
import { PagePaths } from '@/routes/page-paths';
import { Sidebar } from '@/shared/design-system/components/Sidebar';
import {
  NON_ACCREDITED_GATED_PATHS,
  NonAccreditedSection,
  useIsAccreditationGated,
} from '@/shared/modules/accredited-investor-gate';
import { Permission, usePermissions } from '@/shared/modules/permissions';
import { SuiWalletProvider } from '@/shared/modules/wallet/sui-wallet-provider';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

import { GeneralLoadingFallback } from '../general-loading-fallback';
import { Header } from './header';
import { HeaderPage } from './header/components/header-page';
import type { MainLayoutOutletContext } from './outlet-context';
import {
  LayoutRoot,
  MainContent,
  MainWrapper,
  PageBody,
  PipelineMainWrapper,
  ShellColumn,
} from './styled';

export const MainLayout: FC = () => {
  useScrollToTop({
    exclude: [
      (pathname) => pathname === PagePaths.setupFund || /^\/setup-fund\/[^/]+$/.test(pathname),
    ],
  });
  const { user, isPending } = useGetUserProfile();
  const isAccreditationGated = useIsAccreditationGated();
  const isMobile = useIsMobile();
  const { pathname, search } = useLocation();
  const { selectedUserRole } = useSelectedUserConfigSlice();
  const { perspective: boardPerspective, isLoading: boardPerspectiveLoading } =
    useDealBoardViewerPerspective();
  const { has, isLoading: permissionsLoading } = usePermissions();
  const isEntityPreIpoV2 = useFeatureFlag(FeatureFlags.PREIPO_V2_ENTITY);
  // `pipeline` is the old provider-board tab (and the v2 shell's redirect source); `my-pipeline`
  // is the board only while v2 is on, otherwise the URL is about to be sent to the old default.
  const entityProviderBoardTabs: string[] = isEntityPreIpoV2
    ? [DEALS_TAB_KEYS.PIPELINE, PRE_IPO_TABS.MY_PIPELINE]
    : [DEALS_TAB_KEYS.PIPELINE];
  const [headerBackButtonHidden, setHeaderBackButtonHidden] = useState(false);
  const isSettingsPage = matchPath(PagePaths.settings, pathname);
  // Both boards are full-bleed: their columns scroll horizontally edge to edge, so the
  // wrapper padding that suits ordinary pages would clip them.
  const isAdminPipelineTab =
    selectedUserRole === 'admin' &&
    new URLSearchParams(search).get('tab') === DEALS_TAB_KEYS.PIPELINE &&
    !permissionsLoading &&
    has(Permission.KanbanItemView);
  const params = new URLSearchParams(search);
  const isScopedPipelineTab =
    matchPath(PagePaths.deals, pathname) &&
    !boardPerspectiveLoading &&
    ((selectedUserRole === 'user' &&
      params.get('filter') === 'my-pipeline' &&
      boardPerspective === 'Introducer') ||
      (selectedUserRole === 'entity' &&
        entityProviderBoardTabs.includes(params.get('tab') ?? '') &&
        boardPerspective === 'Provider'));
  const isScopedPipelineAlias = Boolean(matchPath(PagePaths.dealsPipeline, pathname));
  const isDealsKanbanPage = Boolean(
    matchPath(PagePaths.dealsKanban, pathname) ||
    isScopedPipelineAlias ||
    (matchPath(PagePaths.deals, pathname) && (isAdminPipelineTab || isScopedPipelineTab)),
  );
  const allFundsPaths = [PagePaths.allFunds, PagePaths.allFundsId, PagePaths.allFundsFeeder];
  const isAllFundsRoute = allFundsPaths.some((p) =>
    Boolean(matchPath({ path: p, end: true }, pathname)),
  );
  const isNonAccreditedGatedRoute = NON_ACCREDITED_GATED_PATHS.some((p) =>
    Boolean(matchPath({ path: p, end: true }, pathname)),
  );
  const MainContentWrapper =
    isAdminPipelineTab || isScopedPipelineTab ? PipelineMainWrapper : MainWrapper;

  if (isPending) {
    return <GeneralLoadingFallback />;
  }

  if (user?.isBlocked && isAllFundsRoute) {
    return <Navigate to={PagePaths.portfolio} replace />;
  }

  const showNonAccreditedGate = isAccreditationGated && isNonAccreditedGatedRoute;

  return (
    <SuiWalletProvider>
      <Suspense fallback={<GeneralLoadingFallback />}>
        <LayoutRoot>
          {!isMobile && <Sidebar bottomSlot={<GetAppBanner />} />}
          <MainContent>
            <ShellColumn>
              <Header />

              <PageBody>
                <HeaderPage hideBackButton={headerBackButtonHidden} isMobile={isMobile} />

                <MainContentWrapper
                  sx={{
                    zIndex: 0,
                    ...((isSettingsPage || isDealsKanbanPage) && !isMobile && { padding: 0 }),
                  }}
                >
                  {showNonAccreditedGate ? (
                    <NonAccreditedSection />
                  ) : (
                    <Outlet
                      context={
                        {
                          setHeaderBackButtonHidden,
                        } satisfies MainLayoutOutletContext
                      }
                    />
                  )}
                </MainContentWrapper>
              </PageBody>
            </ShellColumn>
          </MainContent>
        </LayoutRoot>
      </Suspense>
    </SuiWalletProvider>
  );
};
